import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, CircleHelp, ExternalLink, Keyboard, RotateCcw, Search, X } from 'lucide-react';
import { useExtensionStore } from '../extensions/store';
import { openExternal } from '../utils/openExternal';
import { setSettingsInitialTab, setSettingsModalOpen } from '../store/uiModalStore';
import { replayOnboarding } from '../onboarding/onboardingState';
import { docsFor, indexDocs, searchPages } from './helpContent';
import { useHelpStore } from './helpStore';
import HelpNav from './HelpNav';
import HelpArticle from './HelpArticle';
import HelpToc from './HelpToc';
import { isPaletteOpen } from '../components/palette/paletteOpenState';
import { getModKeyLabel, isEditableTarget } from '../utils/keyboard';

const WEB_DOCS = 'https://apiboo.com/docs';
const EMPTY = [];

/**
 * Help & docs (plan §2): a portalled modal on the shared modal surface.
 * Header: search (Ctrl/Cmd+F or "/" inside Help) and close. Three columns: sections,
 * the article, "On this page" with scroll-spy. Footer: Replay onboarding,
 * Shortcuts, Report an issue, Open on apiboo.com/docs.
 *
 * `openHelp('page#heading')` (helpStore) lands here: the page opens and the
 * article scrolls to the heading.
 */
export default function HelpModal({ onClose, onReport, reportOpen }) {
  const { t, i18n } = useTranslation();
  const { pageId: targetPage, headingId: targetHeading, nonce } = useHelpStore();
  const hidden = useExtensionStore((s) => s.railHidden) || EMPTY;
  const { sections, pages, byId } = useMemo(() => indexDocs(docsFor(i18n.language)), [i18n.language]);

  const [pageId, setPageId] = useState(() => (byId.has(targetPage) ? targetPage : pages[0]?.id));
  const [query, setQuery] = useState('');
  const [activeHeading, setActiveHeading] = useState(null);
  const [scrollTo, setScrollTo] = useState(null);
  const searchRef = useRef(null);
  const articleRef = useRef(null);

  const page = byId.get(pageId) || pages[0];
  const matches = useMemo(() => searchPages(pages, query), [pages, query]);

  const navigate = useCallback(
    (target) => {
      const [p, h] = String(target || '').split('#');
      if (p && byId.has(p)) setPageId(p);
      setScrollTo({ heading: h || null, at: Date.now() });
    },
    [byId]
  );

  // A deep link (or the same one again) from openHelp().
  useEffect(() => {
    if (targetPage && byId.has(targetPage)) {
      setPageId(targetPage);
      setScrollTo({ heading: targetHeading, at: nonce });
    }
  }, [targetPage, targetHeading, nonce, byId]);

  // After the page renders: scroll to the requested heading, else the top.
  useLayoutEffect(() => {
    const el = articleRef.current;
    if (!el || !scrollTo) return;
    const target = scrollTo.heading
      ? [...el.querySelectorAll('[data-heading-id]')].find((n) => n.getAttribute('data-heading-id') === scrollTo.heading)
      : null;
    el.scrollTop = target ? Math.max(0, target.offsetTop - 16) : 0;
    setActiveHeading(target ? scrollTo.heading : page?.headings?.[0]?.id || null);
  }, [scrollTo, pageId, page]);

  const onArticleScroll = () => {
    const el = articleRef.current;
    if (!el) return;
    let current = page?.headings?.[0]?.id || null;
    for (const node of el.querySelectorAll('[data-heading-id]')) {
      if (node.offsetTop - 80 <= el.scrollTop) current = node.getAttribute('data-heading-id');
    }
    setActiveHeading(current);
  };

  // Ctrl/Cmd+F or "/" focuses the search (docs-site convention); Ctrl/Cmd+K
  // is the app-wide command palette and opens OVER Help (palette PLAN §2.10:
  // "everywhere" — Help no longer takes it). Escape closes Help unless a dialog
  // sits on top, the palette included.
  useEffect(() => {
    const onKey = (e) => {
      if (isPaletteOpen() || e.target?.closest?.('[data-palette-root]')) return;
      const typing = isEditableTarget(e.target);
      const find = (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && String(e.key || '').toLowerCase() === 'f';
      const slash = e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !typing;
      if (find || slash) {
        e.preventDefault();
        e.stopImmediatePropagation();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (e.key === 'Escape' && !reportOpen) {
        e.stopPropagation();
        onClose();
      }
    };
    // WINDOW capture: before the app's document-capture shortcut handlers.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, reportOpen]);

  const ctx = useMemo(
    () => ({
      navigate,
      linkLabel: (target) => {
        const [p, h] = target.split('#');
        const linked = byId.get(p);
        if (!linked) return target;
        const heading = h && linked.headings?.find((x) => x.id === h);
        return heading ? `${linked.title} › ${heading.text}` : linked.title;
      },
    }),
    [navigate, byId]
  );

  return (
    <div className="help-overlay">
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="modal-content help-card" role="dialog" aria-modal="true" aria-labelledby="help-title" data-testid="help-modal">
        <div className="help-head">
          <h3 id="help-title">
            <CircleHelp size={16} aria-hidden="true" />
            {t('help.title')}
          </h3>
          <label className="help-search">
            <Search size={15} aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              className="modal-input-no-focus-outline"
              value={query}
              placeholder={t('help.searchPlaceholder')}
              aria-label={t('help.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && matches[0]) navigate(matches[0].id);
              }}
              spellCheck={false}
            />
            <kbd>{getModKeyLabel()}</kbd>
            <kbd>F</kbd>
          </label>
          <button type="button" className="help-x" onClick={onClose} aria-label={t('help.close')}>
            <X size={16} />
          </button>
        </div>

        <div className="help-cols">
          <div className="help-nav panel-scroll">
            <HelpNav sections={sections} matches={matches} currentId={page?.id} hidden={hidden} onOpen={navigate} query={query} />
          </div>
          <div className="help-article panel-scroll" ref={articleRef} onScroll={onArticleScroll}>
            <HelpArticle page={page} byId={byId} ctx={ctx} />
          </div>
          <div className="help-toc panel-scroll">
            <HelpToc headings={page?.headings} activeId={activeHeading} onJump={(id) => navigate(`${page.id}#${id}`)} />
          </div>
        </div>

        <div className="help-foot">
          <button
            type="button"
            className="help-btn"
            onClick={() => {
              onClose();
              replayOnboarding();
            }}
          >
            <RotateCcw size={15} aria-hidden="true" />
            {t('help.replay')}
          </button>
          <button
            type="button"
            className="help-btn"
            onClick={() => {
              onClose();
              setSettingsInitialTab('keyboard');
              setSettingsModalOpen(true);
            }}
          >
            <Keyboard size={15} aria-hidden="true" />
            {t('help.shortcuts')}
          </button>
          <button type="button" className="help-btn" onClick={onReport}>
            <Bug size={15} aria-hidden="true" />
            {t('help.report')}
          </button>
          <span className="help-foot-sp" />
          <button
            type="button"
            className="help-btn"
            onClick={() => openExternal(page ? `${WEB_DOCS}/${page.section.id}/${page.id}` : WEB_DOCS)}
          >
            <ExternalLink size={15} aria-hidden="true" />
            {t('help.openWeb')}
          </button>
        </div>
      </div>
    </div>
  );
}
